import { useEffect, useState } from "react";
import { Tooltip } from "@heroui/react";
import {
  EditableSelectComponent,
  EditableTextareaComponent,
  SectionHeader,
} from "components/index";
import {
  SmokeDetectorsOnEveryFloorStatus,
  FireSecurityUnitInfoDto,
  FireProofDoorsStatus,
} from "shared/types/fire-security";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { AlertInfo, AlertTriangle } from "assets/icons";

const FireSecurityUnitInfoComponent = ({
  fireSecurityUnitInfo,
  handleFireSecurityInfoChangeInParent,
  handleSaveInParent,
  handleCloseParent,
  onEditClick,
  parentEditMode = true, // isLoading = false,
  // building,
}: any) => {
  const [editMode, setEditMode] = useState<boolean>(parentEditMode || true);
  const [editedFireSecurityInfo, setEditedFireSecurityInfo] =
    useState<FireSecurityUnitInfoDto | null>(null);
  // const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
  // const [selectedFileLabel, setSelectedFileLabel] = useState<FileLabel | null>(
  //     null,
  // );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { session } = useSession();

  const getStatusColor = (status: any) => {
    switch (status) {
      case SmokeDetectorsOnEveryFloorStatus.OK:
      case FireProofDoorsStatus.OK:
      case FireProofDoorsStatus.NOT_REQUIRED:
        return "bg-green-500";
      case SmokeDetectorsOnEveryFloorStatus.PARTIAL:
        return "bg-orange-500";
      case SmokeDetectorsOnEveryFloorStatus.NOT_OK:
      case FireProofDoorsStatus.NOT_OK:
        return "bg-red-500";
      default:
        return "bg-transparent";
    }
  };

  useEffect(() => {
    setEditedFireSecurityInfo(fireSecurityUnitInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch

  const localOnEditClick = () => {
    setEditMode((prevMode) => !prevMode);
    if (!editMode) {
      setEditedFireSecurityInfo(fireSecurityUnitInfo);
    }
  };

  useEffect(() => {
    setEditMode(parentEditMode);
  }, [parentEditMode]);

  const updateFireSecurityInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedFireSecurityInfo((prev: any) => ({ ...prev, ...updatedFields }));

    if (handleFireSecurityInfoChangeInParent) {
      handleFireSecurityInfoChangeInParent({
        ...fireSecurityUnitInfo,
        ...updatedFields,
      });
    }
  };

  const onUpdateFireSecurityRemarks = (remarks: string) => {
    updateFireSecurityInfo({ remarks: remarks });
  };

  const handleSave = async () => {
    setHasUnsavedChanges(false);

    if (handleSaveInParent) {
      handleSaveInParent();
    } else {
      try {
        await fetchWithAuth(
          `${API_URL}/fire-security/unit-info`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(editedFireSecurityInfo),
          },
          session,
        );
        toast.success("Brandveiligheidsgegevens succesvol bijgewerkt!");
      } catch (e) {
        console.error(e);
        toast.error(
          "Er is een fout opgetreden bij het bijwerken van de brandveiligheidsgegevens.",
        );
      }
    }
  };

  return (
    <section
      key="fireSecurity"
      className="flex flex-col gap-0 rounded-md bg-white"
    >
      <SectionHeader
        title="Brandveiligheid"
        icon={<AlertTriangle />}
        sticky={false}
        editMode={editMode}
        onEditClick={onEditClick || localOnEditClick}
        onSaveClick={() => handleSave()}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={() => {
          if (handleCloseParent) handleCloseParent();
        }}
        hideCloseAction={true}
      />
      <div className="gap-3 sm:grid sm:grid-cols-2">
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.smokeDetectorsOnEveryFloorStatus,
              )}`}
            />
            <p>Rookmelders op elke verdieping?</p>
            <Tooltip
              content="De rookmelder moet CE gemarkeerd zijn en voldoen aan de norm NBN EN 14604.
              Zowel een type met vervangbare batterij als een type voorzien van een niet-vervangbare batterij komt in aanmerking. Een type met niet-vervangbare batterij met een levensduur van 10 jaar wordt aangeraden.
              De rookmelder moet reageren op de rookontwikkeling bij brand door het produceren van een scherp geluidsignaal.
              De rookmelder mag niet van het ionische type zijn. Dergelijke rookmelders mogen al enkele jaren niet meer verkocht worden.
              Kies een toestel dat beschikt over een testknop."
            >
              <AlertInfo />
            </Tooltip>
          </header>
          <EditableSelectComponent
            label="Selecteer een optie"
            value={
              editedFireSecurityInfo?.smokeDetectorsOnEveryFloorStatus || ""
            }
            options={Object.values(SmokeDetectorsOnEveryFloorStatus).map(
              (value) => ({
                label: value,
                value,
              }),
            )}
            editMode={editMode}
            onChange={(newStatus) =>
              updateFireSecurityInfo({
                smokeDetectorsOnEveryFloorStatus: newStatus,
              })
            }
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.fireProofDoors,
              )}`}
            />
            <p>Brandwerende voordeur?</p>
          </header>
          <EditableSelectComponent
            label="Selecteer een optie"
            value={editedFireSecurityInfo?.fireProofDoors || ""}
            options={Object.values(FireProofDoorsStatus).map((value) => ({
              label: value,
              value,
            }))}
            editMode={editMode}
            onChange={(newStatus) =>
              updateFireSecurityInfo({
                fireProofDoors: newStatus,
              })
            }
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <p>Bijkomende opmerkingen?</p>
          </header>
          <EditableTextareaComponent
            label="Geef opmerkingen of toelichting hier"
            value={editedFireSecurityInfo?.remarks || ""}
            isLoading={false}
            editMode={editMode}
            onChange={onUpdateFireSecurityRemarks}
          />
        </div>
      </div>
      {/* Additional UI elements as required */}
    </section>
  );
};

export default FireSecurityUnitInfoComponent;
